/**
 * AudioManager - Retro sound effects for game actions
 * Uses Web Audio API for lightweight, retro 8-bit style sounds
 */

export type SoundType = 'positive' | 'negative' | 'neutral';

export class AudioManager {
  private audioContext: AudioContext | null = null;
  private enabled: boolean = true;
  private volume: number = 0.3; // Default volume (0-1)

  constructor() {
    // Lazy initialize AudioContext on first use (browser requirement)
    this.enabled = this.loadPreference();
  }

  private loadPreference(): boolean {
    const saved = localStorage.getItem('paperclips-audio-enabled');
    return saved !== 'false'; // Default to enabled
  }

  private savePreference() {
    localStorage.setItem('paperclips-audio-enabled', this.enabled.toString());
  }

  private getContext(): AudioContext {
    if (!this.audioContext) {
      this.audioContext = new (window.AudioContext || window.webkitAudioContext!)();
    }
    return this.audioContext;
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  setEnabled(enabled: boolean) {
    this.enabled = enabled;
    this.savePreference();
  }

  setVolume(volume: number) {
    this.volume = Math.max(0, Math.min(1, volume));
  }

  /**
   * Play a simple retro beep sound
   * @param frequency - Frequency in Hz (higher = higher pitch)
   * @param duration - Duration in seconds
   * @param type - Waveform type ('sine', 'square', 'sawtooth', 'triangle')
   */
  private playBeep(frequency: number, duration: number, type: OscillatorType = 'square') {
    if (!this.enabled) return;

    try {
      const ctx = this.getContext();
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);

      oscillator.frequency.value = frequency;
      oscillator.type = type;

      // Envelope: quick attack, sustain, quick release
      gainNode.gain.setValueAtTime(0, ctx.currentTime);
      gainNode.gain.linearRampToValueAtTime(this.volume, ctx.currentTime + 0.01);
      gainNode.gain.setValueAtTime(this.volume, ctx.currentTime + duration - 0.01);
      gainNode.gain.linearRampToValueAtTime(0, ctx.currentTime + duration);

      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + duration);
    } catch (e) {
      console.warn('AudioManager: Failed to play sound', e);
    }
  }

  /**
   * Play a multi-tone sequence (arpeggio)
   */
  private playSequence(frequencies: number[], noteDuration: number, type: OscillatorType = 'square') {
    if (!this.enabled) return;

    frequencies.forEach((freq, index) => {
      setTimeout(() => {
        this.playBeep(freq, noteDuration, type);
      }, index * noteDuration * 1000);
    });
  }

  // ============================================
  // POSITIVE SOUNDS (Success, Gain, Achievement)
  // ============================================

  /**
   * Click sound - neutral, brief feedback
   */
  playClick() {
    this.playBeep(800, 0.05, 'square');
  }

  /**
   * Clip made - subtle positive feedback
   */
  playClipMade() {
    this.playBeep(440, 0.08, 'sine');
  }

  /**
   * Purchase success - rising tone
   */
  playPurchase() {
    this.playSequence([523, 659], 0.08, 'triangle');
  }

  /**
   * Unlock/Achievement - celebratory arpeggio
   */
  playUnlock() {
    this.playSequence([523, 659, 784, 1047], 0.1, 'square');
  }

  /**
   * Milestone - major achievement fanfare
   */
  playMilestone() {
    this.playSequence([523, 659, 784, 1047, 1319], 0.12, 'triangle');
  }

  /**
   * Money gained - coin sound
   */
  playMoneyGain() {
    this.playSequence([1047, 1319], 0.06, 'sine');
  }

  /**
   * Project completed - success jingle
   */
  playProjectComplete() {
    this.playSequence([659, 784, 880, 1047], 0.1, 'square');
  }

  // ============================================
  // NEGATIVE SOUNDS (Error, Failure, Warning)
  // ============================================

  /**
   * Error - descending tone
   */
  playError() {
    this.playSequence([400, 300], 0.1, 'sawtooth');
  }

  /**
   * Cannot afford - gentle rejection
   */
  playCannotAfford() {
    this.playBeep(200, 0.15, 'sawtooth');
  }

  /**
   * Warning - attention grabber
   */
  playWarning() {
    this.playSequence([600, 400, 600], 0.12, 'square');
  }

  /**
   * Danger - urgent alert
   */
  playDanger() {
    this.playSequence([800, 600, 800, 600], 0.1, 'sawtooth');
  }

  /**
   * Loss - sad descending tone
   */
  playLoss() {
    this.playSequence([523, 440, 349, 262], 0.12, 'triangle');
  }

  // ============================================
  // NEUTRAL SOUNDS (Interface, Ambient)
  // ============================================

  /**
   * Button press - UI feedback
   */
  playButton() {
    this.playBeep(600, 0.06, 'square');
  }

  /**
   * Panel open/close
   */
  playPanel() {
    this.playBeep(700, 0.08, 'sine');
  }

  /**
   * Tick - periodic update
   */
  playTick() {
    this.playBeep(1200, 0.03, 'sine');
  }

  /**
   * Notification - gentle alert
   */
  playNotification() {
    this.playSequence([659, 880], 0.1, 'sine');
  }

  /**
   * Modal open
   */
  playModalOpen() {
    this.playSequence([440, 554, 659], 0.08, 'triangle');
  }

  /**
   * Modal close
   */
  playModalClose() {
    this.playSequence([659, 554, 440], 0.08, 'triangle');
  }

  // ============================================
  // SPECIAL SOUNDS (Phase transitions, dramatic moments)
  // ============================================

  /**
   * Phase transition - epic ascending arpeggio
   */
  playPhaseTransition() {
    this.playSequence([262, 330, 392, 523, 659, 784, 1047], 0.15, 'square');
  }

  /**
   * Endgame - mysterious/ominous
   */
  playEndgame() {
    this.playSequence([220, 233, 247, 262], 0.2, 'sawtooth');
  }

  /**
   * Victory - triumphant fanfare
   */
  playVictory() {
    this.playSequence([523, 659, 784, 1047, 1319, 1568, 2093], 0.12, 'triangle');
  }

  /**
   * Combat - aggressive beeps
   */
  playCombat() {
    this.playSequence([880, 784, 880, 784], 0.08, 'sawtooth');
  }

  /**
   * Probe launch - ascending whoosh
   */
  playProbeLaunch() {
    this.playSequence([200, 300, 400, 600, 800], 0.1, 'sine');
  }
}

// Singleton instance
export const audioManager = new AudioManager();
